import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, DialogContent, Grid, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {EventData, EventFormMode} from '@/types/event';
import {useAppSelector} from '@/redux-store/store';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import DialogWithCloseIcon from '@/components/ui/dialogs/dialog-with-close-icon';
import {useSnackbar} from '@/contexts/snackbar';
import {getEventById} from '@/rest/events/get-event';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import EventInfo from './event-info';
import EventDetailsButtons from './buttons/event-buttons';
import {EventLocation} from '@/components/locations/event-location';
import EventParticipatingCard from './event-participating-card';
import EventJoinButton from './buttons/event-join-button';
import ParticipantsList from '@/components/participant/participants-list';
import OnlineEventCard from './online-event-card';
import {
  cancelEventFunc,
  deleteEventFunc,
  joinEventFunc,
  leaveEventFunc
} from './event-actions';
import {useDispatch} from 'react-redux';
import {useCompanyContext} from '@/contexts/admin/companies';
import {useMatrixContext} from '@/contexts/matrix/client';
import {useMatrixRoomContext} from '@/contexts/matrix/room';
import KeycloakInstance from '@/services/keycloak';
import {checkPaymentStatus} from '@/rest/payments/check-payment-status';
import PaymentScreen from '@/components/payments/payment-screen';
import {loadStripe} from '@stripe/stripe-js';
import {env} from '@/config';
import {Elements} from '@stripe/react-stripe-js';
import {createPayment} from '@/rest/payments/create-payment';
import EventForm from '@/components/forms/events';
import {resetEventFormData} from '@/redux-store/slices/events';
import BillingDataForm from '@/components/forms/user/billing-data';
import {requestRefund} from '@/rest/payments/request-refund';
import {
  formatPrice,
  getCompanyLabel,
  isDocumentCompanyRoleHealthManager,
  isOneOfUserCompaniesDocument,
  isUserDocumentManager
} from '@/utils';
import {mapFrontendBillingDataToBackend} from '@/services/billing-data';
import {resetSubscriptionData} from '@/redux-store/slices/billing-data';
import OrderSummary from '@/components/payments/order-summary';
import {cancelPayment} from '@/rest/payments/cancel-payment';
import GroupDetailsDialog from '@/components/groups/details';
import {MatrixRoomProvider} from '@/contexts/matrix/room/provider';
import MatrixUnreadEventsProvider from '@/contexts/matrix/unread-events/provider';
import {GroupData} from '@/types/group';
import {getGroupById} from '@/rest/groups/get-group';
import {
  setCurrentEvent,
  setCurrentOnDemandEvent
} from '@/redux-store/slices/auth';
import {AppVariant, DevicePlatform} from '@/types/common';
import {useNavigate} from 'react-router-dom';
import PaymentMethods from '@/components/payments/payment-methods';
import {isEventInPast, isUserEventParticipant} from '@/services/events';

interface EventDetailsDialogProps {
  open: boolean;
  event: EventData;
  onClose: () => void;
  onChange?: (data: EventData) => void;
  onDelete?: (eventId: string) => void;
  onClone?: () => void;
  myGroupAsDefaultInCloneForm?: boolean;
  groupOfEventChanged?: () => void;
  defaultChatTab?: boolean;
  groupId?: string;
  onGroupClose?: () => void;
  onCloseGroupDetails?: () => void;
}

const EventDetailsDialog: React.FC<EventDetailsDialogProps> = ({
  open,
  event,
  onClose,
  onChange,
  onDelete,
  onClone,
  myGroupAsDefaultInCloneForm = false,
  groupOfEventChanged,
  defaultChatTab = false,
  groupId,
  onGroupClose,
  onCloseGroupDetails
}) => {
  const {t} = useTranslation();

  const {STRIPE_PUBLISHABLE_KEY, APP_VARIANT} = env;

  const {showSnackbarMessage} = useSnackbar();

  const navigate = useNavigate();

  const {matrixClient} = useMatrixContext();
  const {
    initMatrixRoom,
    userDialogOpen,
    multimediaDialogOpen,
    handleSendDirectMessage
  } = useMatrixRoomContext();

  const {
    isAuthenticated,
    matrixCredentials,
    isUserHealthManager,
    keycloakUser,
    currentEvent,
    currentOnDemandEvent
  } = useAppSelector((state) => state.auth);
  const eventFilter = useAppSelector((state) => state.filter.event);
  const userData = useAppSelector((state) => state.userInfos.userInfos);
  const {billingData} = useAppSelector((state) => state.billingData);
  const {refreshCompanyData} = useCompanyContext() || {};
  const trainerInSjOne =
    keycloakUser?.userRole === 'TRAINER' && APP_VARIANT === 'sjone';
  const isAdmin = keycloakUser?.userRole === 'ADMIN';

  const [cloneEventFormOpen, setCloneEventFormOpen] = useState(false);
  const [editEventFormOpen, setEditEventFormOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventData | null>(event);
  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [confirmPaymentDialogOpen, setConfirmPaymentDialogOpen] =
    useState(false);
  const [hasPaid, setHasPaid] = useState<boolean>(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [billingDataFormOpen, setBillingDataFormOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundRequestPending, setRefundRequestPending] = useState(false);
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupData | null>(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);

  const pastEvent = selectedEvent && isEventInPast(selectedEvent);

  const companyLabel =
    event.company && getCompanyLabel(event.company, userData.companies || []);

  let stripePromise;

  if (selectedEvent && selectedEvent.organizerAccountId) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: selectedEvent.organizerAccountId
    });
  }

  const isUserParticipant = isUserEventParticipant(selectedEvent, userData._id);

  const isUserManager = isUserDocumentManager(selectedEvent, userData);

  const trainerInSjOneNoEventManager = trainerInSjOne && !isUserManager;

  const isHealthManagerEvent =
    selectedEvent && isDocumentCompanyRoleHealthManager(selectedEvent);

  const companyEventAndUserHealthManager =
    isUserHealthManager && isHealthManagerEvent;

  const isUserAuthorizedToSeeRoom = useMemo(() => {
    return !!isUserParticipant;
  }, [isUserParticipant]);

  const isUserAuthorizedToEditRoom = useMemo(() => {
    return !!(isUserParticipant || companyEventAndUserHealthManager);
  }, [companyEventAndUserHealthManager, isUserParticipant]);

  const maxParticipants = selectedEvent && selectedEvent.maxParticipants;

  const participantsCount =
    (selectedEvent && selectedEvent.participants.length) || 0;

  const isEventFull =
    maxParticipants !== null && participantsCount >= maxParticipants;

  const eventCancelled = event.status === 'CANCELLED';

  const dispatch = useDispatch();

  const handleDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(true);
  }, []);

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleCancelConfirmation = useCallback(() => {
    setCancelConfirmationOpen(true);
  }, []);

  const handleCloseCancelConfirmation = useCallback(() => {
    setCancelConfirmationOpen(false);
  }, []);

  const handleCloseSelectedEvent = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleJoinEvent = useCallback(async () => {
    await joinEventFunc(
      selectedEvent,
      showSnackbarMessage,
      t,
      setSelectedEvent,
      onChange
    );
  }, [onChange, selectedEvent, showSnackbarMessage, t]);

  const handleJoinClick = useCallback(async () => {
    if (!isAuthenticated) {
      KeycloakInstance.loginExternal().catch((error) => {
        console.error('Login failed: ', error);
      });
      return;
    }

    if (selectedEvent?.isPaid) {
      if (hasPaid || isUserManager) {
        handleJoinEvent();
      } else {
        setConfirmPaymentDialogOpen(true);
      }
    } else {
      handleJoinEvent();
    }
  }, [
    isAuthenticated,
    selectedEvent?.isPaid,
    hasPaid,
    isUserManager,
    handleJoinEvent
  ]);

  const handleCloseConfirmPaymentDialog = useCallback(() => {
    setConfirmPaymentDialogOpen(false);
  }, []);

  const handleOpenPaymentDialog = useCallback(async () => {
    try {
      if (selectedEvent) {
        const {price, currency, _id} = selectedEvent;

        const billingDataToSend = mapFrontendBillingDataToBackend(billingData);

        const paymentData = await createPayment({
          amount: price,
          currency,
          eventId: _id,
          appVariant: APP_VARIANT as AppVariant,
          devicePlatform: DevicePlatform.WEB,
          billingData: billingDataToSend || undefined
        });

        if (paymentData?.clientSecret) {
          dispatch(resetSubscriptionData());
          setClientSecret(paymentData.clientSecret);
          setPaymentDialogOpen(true);
        } else {
          showSnackbarMessage(t('event_payment_failed'), 'error');
        }
      }
    } catch (error) {
      console.error('Error while creating payment: ', error);
    }
  }, [
    selectedEvent,
    showSnackbarMessage,
    billingData,
    dispatch,
    APP_VARIANT,
    t
  ]);

  const handleConfirmPaymentAndJoin = useCallback(async () => {
    handleCloseConfirmPaymentDialog();
    if (selectedEvent) {
      setBillingDataFormOpen(true);
    }
  }, [handleCloseConfirmPaymentDialog, selectedEvent]);

  const handleCloseRefundDialog = useCallback(() => {
    setRefundDialogOpen(false);
  }, []);

  const handleSendRefundRequest = useCallback(async () => {
    if (selectedEvent) {
      const organizerMail = selectedEvent.managers[0].email;

      await requestRefund(selectedEvent._id);

      const messageData = {
        key: 'request_refund_message',
        params: {
          eventName: selectedEvent.title,
          name: userData.name,
          email: userData.email
        }
      };

      const messageString = JSON.stringify(messageData);

      handleSendDirectMessage(messageString, organizerMail);

      handleCloseRefundDialog();

      await leaveEventFunc(
        selectedEvent,
        showSnackbarMessage,
        t,
        setSelectedEvent,
        onChange
      );
    }
  }, [
    handleCloseRefundDialog,
    handleSendDirectMessage,
    onChange,
    selectedEvent,
    showSnackbarMessage,
    t,
    userData
  ]);

  const handleLeaveClick = useCallback(async () => {
    if (selectedEvent && selectedEvent.isPaid && !isUserManager) {
      setRefundDialogOpen(true);

      return;
    }
    await leaveEventFunc(
      selectedEvent,
      showSnackbarMessage,
      t,
      setSelectedEvent,
      onChange
    );
  }, [onChange, selectedEvent, showSnackbarMessage, t, isUserManager]);

  const handleDeleteClick = useCallback(async () => {
    await deleteEventFunc(
      selectedEvent,
      handleCloseDeleteConfirmation,
      showSnackbarMessage,
      t,
      dispatch,
      handleCloseSelectedEvent,
      refreshCompanyData,
      onDelete
    );
  }, [
    selectedEvent,
    handleCloseDeleteConfirmation,
    showSnackbarMessage,
    t,
    dispatch,
    handleCloseSelectedEvent,
    onDelete,
    refreshCompanyData
  ]);

  const handleCancelClick = useCallback(async () => {
    const eventResponse = await cancelEventFunc(
      selectedEvent,
      handleCloseCancelConfirmation,
      showSnackbarMessage,
      t,
      onChange
    );
    if (eventResponse) {
      setSelectedEvent(eventResponse);
    }
  }, [
    selectedEvent,
    handleCloseCancelConfirmation,
    showSnackbarMessage,
    t,
    onChange
  ]);

  const onEventEdited = useCallback(async () => {
    showSnackbarMessage(t('event_update_success'), 'success');
    setEditEventFormOpen(false);

    if (onClone) {
      onClone();
    }
    getEventById(event._id).then((updatedEvent) => {
      setSelectedEvent(updatedEvent);
    });
  }, [showSnackbarMessage, t, onClone, event._id]);

  const onEventEditError = useCallback(() => {
    showSnackbarMessage(t('event_update_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleEditButtonClick = useCallback(() => {
    setEditEventFormOpen(true);
  }, []);

  const handleEventEditCancel = useCallback(() => {
    dispatch(resetEventFormData());
    setEditEventFormOpen(false);
  }, [dispatch]);

  const handleCloneButtonClick = useCallback(() => {
    setCloneEventFormOpen(true);
  }, []);

  const handleEventCloneCancel = useCallback(() => {
    dispatch(resetEventFormData());
    setCloneEventFormOpen(false);
  }, [dispatch]);

  const onEventCloned = useCallback(() => {
    showSnackbarMessage(t('event_create_success'), 'success');
    if (onClone) {
      onClone();
      refreshCompanyData();
    }
    setCloneEventFormOpen(false);
  }, [onClone, showSnackbarMessage, t, refreshCompanyData]);

  const onEventCloneError = useCallback(() => {
    showSnackbarMessage(t('event_create_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleUserOverviewOpenChange = useCallback((isOpen: boolean) => {
    setUserOverviewDialogOpen(isOpen);
  }, []);

  const onPaymentError = useCallback(() => {
    setPaymentDialogOpen(false);
    showSnackbarMessage(t('event_payment_failed'), 'error');
  }, [showSnackbarMessage, t]);

  const handleCancelPayment = useCallback(async () => {
    if (!selectedEvent) return;

    try {
      await cancelPayment(selectedEvent._id);
      setPaymentDialogOpen(false);
      showSnackbarMessage(t('event_payment_canceled'), 'error');
    } catch (error) {
      console.error('Error cancelling payment:', error);
      showSnackbarMessage(t('event_payment_failed'), 'error');
    }
  }, [selectedEvent, showSnackbarMessage, t]);

  const onPaymentSuccess = useCallback(() => {
    setPaymentDialogOpen(false);
    handleJoinEvent();
  }, [handleJoinEvent]);

  const handleOpenOrderSummary = useCallback(() => {
    setOrderSummaryOpen(true);
  }, []);

  const onBillingDataFormSubmitted = useCallback(() => {
    setBillingDataFormOpen(false);
    handleOpenOrderSummary();
  }, [handleOpenOrderSummary]);

  const handleBillingDataFormCancel = useCallback(() => {
    setBillingDataFormOpen(false);
    setClientSecret(null);
    showSnackbarMessage(t('event_payment_canceled'), 'error');
  }, [showSnackbarMessage, t]);

  const onBillingDataFormError = useCallback(() => {
    setBillingDataFormOpen(false);
    setClientSecret(null);
    showSnackbarMessage(t('event_payment_failed'), 'error');
  }, [showSnackbarMessage, t]);

  const handleOrderSummaryCancel = useCallback(() => {
    setOrderSummaryOpen(false);
    setClientSecret(null);
    showSnackbarMessage(t('event_payment_canceled'), 'error');
  }, [showSnackbarMessage, t]);

  const onOrderSummaryPassed = useCallback(() => {
    setOrderSummaryOpen(false);
    handleOpenPaymentDialog();
  }, [handleOpenPaymentDialog]);

  const onGoBackToBillingForm = useCallback(() => {
    setOrderSummaryOpen(false);
    setBillingDataFormOpen(true);
  }, []);

  const handleOpenGroupWindow = useCallback(
    async (groupId: string) => {
      const group = await getGroupById(groupId);
      if (group) {
        if (APP_VARIANT === 'sjone') {
          if (!isOneOfUserCompaniesDocument(group, userData)) {
            navigate('/403');
          }
        }
        handleCloseSelectedEvent();
        setSelectedGroup(group);
        setGroupDialogOpen(true);
      } else {
        handleCloseSelectedEvent();
      }
    },

    [APP_VARIANT, handleCloseSelectedEvent, navigate, userData]
  );

  const handleGroupDialogClose = useCallback(() => {
    setGroupDialogOpen(false);
    setSelectedGroup(null);
    setCurrentGroupId(null);
    if (onGroupClose) {
      onGroupClose();
    }
    if (onCloseGroupDetails) {
      onCloseGroupDetails();
    }
  }, [onGroupClose, onCloseGroupDetails]);

  useEffect(() => {
    async function fetchEventDetails() {
      if (event) {
        const {pastEvents} = eventFilter;
        const response = await getEventById(event._id, pastEvents);
        if (response) {
          setSelectedEvent(response);
        }
      }
    }
    fetchEventDetails();
  }, [event, eventFilter]);

  useEffect(() => {
    if (selectedEvent) {
      if (
        isUserAuthorizedToSeeRoom &&
        matrixClient &&
        matrixCredentials &&
        !selectedEvent.onDemand
      ) {
        initMatrixRoom();
      }
    }
  }, [
    selectedEvent,
    matrixClient,
    initMatrixRoom,
    isUserManager,
    isUserParticipant,
    companyEventAndUserHealthManager,
    isUserAuthorizedToSeeRoom,
    isUserAuthorizedToEditRoom,
    matrixCredentials
  ]);

  useEffect(() => {
    const checkUserPaymentStatus = async () => {
      if (
        selectedEvent &&
        selectedEvent.isPaid &&
        isAuthenticated &&
        !isUserParticipant
      ) {
        try {
          const paymentStatus = await checkPaymentStatus(selectedEvent._id);
          if (paymentStatus.intentExists) {
            if (paymentStatus.status === 'succeeded') {
              setHasPaid(true);
            } else if (paymentStatus.status === 'refund_requested') {
              setRefundRequestPending(true);
            }
          }
        } catch (error) {
          console.error('Error checking payment status', error);
        }
      }
    };
    checkUserPaymentStatus();
  }, [isUserParticipant, isAuthenticated, selectedEvent]);

  useEffect(() => {
    if (groupId && selectedEvent) {
      setCurrentGroupId(groupId);
    }
  }, [groupId, selectedEvent]);

  useEffect(() => {
    if (currentGroupId && selectedEvent) {
      handleOpenGroupWindow(currentGroupId);
    }
  });

  useEffect(() => {
    if (
      selectedEvent &&
      currentEvent !== selectedEvent._id &&
      !selectedEvent.onDemand
    ) {
      dispatch(setCurrentEvent(selectedEvent._id));
    }
    if (
      selectedEvent &&
      currentOnDemandEvent !== selectedEvent._id &&
      selectedEvent.onDemand
    ) {
      dispatch(setCurrentOnDemandEvent(selectedEvent._id));
    }
  }, [currentEvent, currentOnDemandEvent, dispatch, selectedEvent]);

  const anyOtherDialogOpen = editEventFormOpen || cloneEventFormOpen;

  return (
    <>
      {selectedEvent && (
        <DialogWithCloseIcon
          open={open && !anyOtherDialogOpen}
          hide={
            userDialogOpen ||
            multimediaDialogOpen ||
            userOverviewDialogOpen ||
            billingDataFormOpen ||
            paymentDialogOpen ||
            orderSummaryOpen ||
            !!groupId
          }
          onClose={onClose}
          fullWidth={true}
          maxWidth={selectedEvent.onDemand ? 'md' : 'lg'}
        >
          <DialogContent>
            <Typography align="center" variant="h6">
              {t('event_detail_headline')}
            </Typography>
            <Grid container spacing={1} direction="row" marginTop={1}>
              <Grid item xs={12} sm={selectedEvent.onDemand ? 12 : 8}>
                <EventInfo
                  onUserOverviewOpenChange={handleUserOverviewOpenChange}
                  selectedEvent={selectedEvent}
                  isUserAuthorizedToSeeRoom={isUserAuthorizedToSeeRoom}
                  isUserParticipant={isUserParticipant || false}
                  onGroupWindowOpen={handleOpenGroupWindow}
                  defaultChatTab={defaultChatTab}
                  formattedPrice={
                    selectedEvent.price !== undefined
                      ? formatPrice(selectedEvent.price)
                      : ''
                  }
                  trainerInSjOne={trainerInSjOne}
                  isUserManager={isUserManager || false}
                  companyName={companyLabel}
                  onEditButtonClick={handleEditButtonClick}
                  onCloneButtonClick={handleCloneButtonClick}
                  onDeleteButtonClick={handleDeleteConfirmation}
                  isUserAdmin={isAdmin}
                />
              </Grid>
              {!selectedEvent.onDemand && (
                <Grid item xs={12} sm={4}>
                  {!selectedEvent.online ? (
                    <Box marginTop={{xs: 2, sm: 0}}>
                      <EventLocation
                        eventLocationCoords={{
                          lat: selectedEvent.geolocation.coordinates[1],
                          lng: selectedEvent.geolocation.coordinates[0]
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: {
                          xs: 'none',
                          sm: 'block'
                        }
                      }}
                    >
                      <OnlineEventCard />
                    </Box>
                  )}

                  {!trainerInSjOneNoEventManager && (
                    <EventDetailsButtons
                      selectedEvent={selectedEvent}
                      pastEvent={pastEvent || false}
                      handleLeaveClick={handleLeaveClick}
                      handleEditButtonClick={handleEditButtonClick}
                      handleCloneButtonClick={handleCloneButtonClick}
                      handleDeleteConfirmation={handleDeleteConfirmation}
                      handleCancelConfirmation={handleCancelConfirmation}
                    />
                  )}

                  {isUserParticipant ? (
                    <EventParticipatingCard />
                  ) : (
                    !eventCancelled &&
                    !trainerInSjOneNoEventManager && (
                      <>
                        <EventJoinButton
                          onClick={handleJoinClick}
                          isEventFull={isEventFull}
                          hasUserPaid={hasPaid}
                          refundRequestPending={refundRequestPending}
                          price={
                            selectedEvent.price !== undefined
                              ? formatPrice(selectedEvent.price)
                              : ''
                          }
                          currency={'€'}
                          isUserManager={isUserManager || undefined}
                        />
                        {selectedEvent.isPaid && <PaymentMethods />}
                      </>
                    )
                  )}
                </Grid>
              )}
            </Grid>
            {!trainerInSjOne && !selectedEvent.onDemand && (
              <Box marginTop={1}>
                <ParticipantsList
                  onUserOverviewOpenChange={handleUserOverviewOpenChange}
                  participants={selectedEvent.participants}
                  participantsLimit={selectedEvent.maxParticipants}
                />
              </Box>
            )}
          </DialogContent>
        </DialogWithCloseIcon>
      )}

      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={t('delete_event_dialog_title')}
        message={
          selectedEvent?.onDemand
            ? t('delete_event_dialog_content_short')
            : t('delete_event_dialog_content')
        }
        onConfirm={handleDeleteClick}
        onCancel={handleCloseDeleteConfirmation}
        warn
      />

      <ConfirmationDialog
        open={cancelConfirmationOpen}
        title={t('cancel_event_dialog_title')}
        message={t('cancel_event_dialog_content')}
        onConfirm={handleCancelClick}
        onCancel={handleCloseCancelConfirmation}
      />

      <ConfirmationDialog
        open={confirmPaymentDialogOpen}
        title={t('confirm_payment_title')}
        message={t('confirm_payment_description')}
        onConfirm={handleConfirmPaymentAndJoin}
        onCancel={handleCloseConfirmPaymentDialog}
        yesButton={t('understood')}
      />

      <ConfirmationDialog
        open={refundDialogOpen}
        title={t('request_refund')}
        message={t('request_refund_description')}
        onConfirm={handleSendRefundRequest}
        onCancel={handleCloseRefundDialog}
      />

      <DialogForm
        open={editEventFormOpen}
        onSubmit={onEventEdited}
        onCancel={handleEventEditCancel}
        onError={onEventEditError}
        FormComponent={EventForm}
        mode={EventFormMode.UPDATE}
        initialValues={selectedEvent}
        maxHeightAllowed={'1200px'}
        groupOfEventChanged={groupOfEventChanged}
        onDemand={selectedEvent?.onDemand}
      />
      <DialogForm
        open={cloneEventFormOpen}
        onSubmit={onEventCloned}
        onCancel={handleEventCloneCancel}
        onError={onEventCloneError}
        mode={EventFormMode.CLONE}
        FormComponent={EventForm}
        initialValues={selectedEvent}
        {...(myGroupAsDefaultInCloneForm
          ? {defaultGroup: selectedEvent?.group._id}
          : {})}
        maxHeightAllowed={'1200px'}
        onDemand={selectedEvent?.onDemand}
      />

      {orderSummaryOpen && selectedEvent && (
        <DialogWithCloseIcon
          open={orderSummaryOpen}
          onClose={handleOrderSummaryCancel}
          maxWidth={'md'}
        >
          <OrderSummary
            event={selectedEvent}
            onConfirm={onOrderSummaryPassed}
            onGoBack={onGoBackToBillingForm}
          />
        </DialogWithCloseIcon>
      )}

      {billingDataFormOpen && selectedEvent && (
        <DialogForm
          open={billingDataFormOpen}
          onSubmit={onBillingDataFormSubmitted}
          onCancel={handleBillingDataFormCancel}
          onError={onBillingDataFormError}
          FormComponent={BillingDataForm}
          withSubscription={selectedEvent.tag === 'pbu'}
          maxHeightAllowed={'1200px'}
          termsOfUseLink={selectedEvent.termsOfUseLink}
          cancellationPolicyLink={selectedEvent.cancellationPolicyLink}
          withGoBackIcon
        />
      )}

      {paymentDialogOpen &&
        selectedEvent &&
        clientSecret &&
        stripePromise &&
        !billingDataFormOpen && (
          <Elements stripe={stripePromise} options={{clientSecret}}>
            <PaymentScreen
              eventPrice={selectedEvent.price.toFixed(2)}
              onError={onPaymentError}
              onSuccess={onPaymentSuccess}
              onCancel={handleCancelPayment}
            />
          </Elements>
        )}

      {selectedGroup && (
        <MatrixUnreadEventsProvider documentId={selectedGroup._id}>
          <MatrixRoomProvider childId={selectedGroup._id}>
            <GroupDetailsDialog
              onClose={handleGroupDialogClose}
              group={selectedGroup}
              open={groupDialogOpen}
              onChange={(newData) => {
                if (selectedGroup._id === newData._id) {
                  setSelectedGroup(newData);
                }
              }}
              onDelete={(groupId) => {
                if (selectedGroup._id === groupId) {
                  setGroupDialogOpen(false);
                  setSelectedGroup(null);
                }
              }}
            />
          </MatrixRoomProvider>
        </MatrixUnreadEventsProvider>
      )}
    </>
  );
};

export default EventDetailsDialog;
